import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Tile from "@src/components/containers/Tile";
import SpinningLoader from "@src/components/common/SpinningLoader/SpinningLoader";
import Modal, { ModalSizes } from "@src/components/common/Modal";
import { useToast } from "@swordsweeper/ssi-ui-component-library";
import {
  useCovetrusLobaConnection,
  CovetrusLobaModalBody,
} from "@src/components/widgets/AccountDistributorSettings/CovetrusConnectionModalContent";
import {
  DISTRIBUTOR_COVETRUS,
  DISTRIBUTOR_NAMES,
} from "@src/constants/Distributors";
import { useCurrentUserStore } from "@src/state/currentUserState";
import { DistributorConnectionStatuses } from "@src/components/widgets/AccountDistributorSettings/DistributorConnectionConstants";
import { CovetrusLobaConnectionPhases } from "@src/components/widgets/AccountDistributorSettings/CovetrusConnectionConstants";
import styles from "./CovetrusAuthCallbackPage.module.scss";

const COVETRUS_DISPLAY_NAME = DISTRIBUTOR_NAMES[DISTRIBUTOR_COVETRUS];

function CovetrusAuthCallbackPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const state = searchParams.get("state");
  const { fetchDistributorConnections, updateDistributorConnectionStatus } =
    useCurrentUserStore();
  const [isConnectionComplete, setIsConnectionComplete] = useState(false);

  const goToDistributors = () => {
    navigate("/account/distributors", { replace: true });
  };

  const handleSuccess = () => {
    updateDistributorConnectionStatus(
      DISTRIBUTOR_COVETRUS,
      DistributorConnectionStatuses.Connected,
    );
    showToast({
      message: `Successfully connected to ${COVETRUS_DISPLAY_NAME}`,
      variant: "success",
    });
    void fetchDistributorConnections({ includeOAuthLink: true });
    setIsConnectionComplete(true);
  };

  const connection = useCovetrusLobaConnection(code ?? "", state);
  const showModal =
    Boolean(code) &&
    !isConnectionComplete &&
    connection.phase !== CovetrusLobaConnectionPhases.Loading;

  const handleSubmit = async (formData: { selectedLobaUid: string }) => {
    const success = await connection.handleConnectSubmit(formData);
    if (success) {
      handleSuccess();
    }
  };

  return (
    <>
      {code && connection.phase === CovetrusLobaConnectionPhases.Loading && (
        <SpinningLoader />
      )}

      {showModal && (
        <Modal
          isShowing
          size={ModalSizes.ExtraLarge}
          className={styles.modal}
          onClose={goToDistributors}
          closeOnBackdropClick={false}
          header={`Connect to ${COVETRUS_DISPLAY_NAME}`}
        >
          <CovetrusLobaModalBody
            phase={connection.phase}
            lobas={connection.lobas}
            errorMessage={connection.errorMessage}
            isSavingLoba={connection.isSavingLoba}
            onClose={goToDistributors}
            onSubmit={handleSubmit}
          />
        </Modal>
      )}

      {isConnectionComplete && (
        <div className="flex-row flex-center margin-top-md">
          <Tile className="width-50 text-center">
            <h4 className="text-success">
              Successfully connected to {COVETRUS_DISPLAY_NAME}!
            </h4>
            <Link
              to="/account/distributors"
              className="text-sm margin-top-md"
              onClick={(event) => {
                event.preventDefault();
                goToDistributors();
              }}
            >
              Back to Distributors
            </Link>
          </Tile>
        </div>
      )}

      {!code && !isConnectionComplete && (
        <div className="flex-row flex-center margin-top-md">
          <Tile className="width-50 text-center">
            <h4 className="text-error">
              Error connecting to {COVETRUS_DISPLAY_NAME}: Missing authorization
              code. Please try connecting again.
            </h4>
            <Link to="/account/distributors" className="text-sm margin-top-md">
              Back to Distributors
            </Link>
          </Tile>
        </div>
      )}
    </>
  );
}

export default CovetrusAuthCallbackPage;
